import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function TeamManagement({ session }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    members: ['', '', '', '']
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/teams/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();

    if (!newTeam.name.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    const validMembers = newTeam.members.filter(m => m.trim());
    if (validMembers.length === 0) {
      toast.error('Please add at least one member');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: newTeam.name,
          members: validMembers
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create team');
      }

      toast.success('Team created successfully! 🎉');
      setShowCreateModal(false);
      setNewTeam({ name: '', members: ['', '', '', ''] });
      fetchTeams();
    } catch (error) {
      toast.error(error.message || 'Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    // Using window.confirm explicitly to avoid eslint warning
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete team');
      }

      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error) {
      toast.error(error.message || 'Failed to delete team');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Loading teams...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ 
            fontSize: '2rem', 
            marginBottom: '10px',
            fontFamily: 'Orbitron, sans-serif',
            color: 'var(--primary-color)'
          }}>
            👥 My Teams
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Create and manage your gaming squads
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          ➕ Create Team
        </button>
      </div>

      {teams.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'var(--secondary-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <p style={{ fontSize: '4rem', marginBottom: '20px' }}>👥</p>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No teams yet</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Create your first team to play squad matches!
          </p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Create Team
          </button>
        </div>
      ) : (
        <div className="grid grid-2">
          {teams.map((team) => (
            <div key={team.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700' }}>{team.name}</h3>
                <span className="badge badge-info">{team.members.length} Members</span>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '0.9rem',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Team Members:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {team.members.map((member, index) => (
                    <div key={index} style={{ 
                      padding: '10px',
                      background: 'var(--accent-bg)',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{ 
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        background: 'var(--primary-color)',
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}>
                        {index + 1}
                      </span>
                      <span>{member}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px',
                paddingTop: '15px',
                borderTop: '1px solid var(--border-color)'
              }}>
                <button 
                  className="btn btn-outline" 
                  style={{ flex: 1, fontSize: '0.9rem', padding: '8px' }}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-danger" 
                  style={{ flex: 1, fontSize: '0.9rem', padding: '8px' }}
                  onClick={() => handleDeleteTeam(team.id)}
                >
                  Delete
                </button>
              </div>

              <div style={{ 
                marginTop: '15px',
                padding: '10px',
                background: 'rgba(0, 240, 255, 0.05)',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: 'var(--text-secondary)'
              }}>
                Created: {new Date(team.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create New Team</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreateTeam}>
              <div className="form-group">
                <label className="form-label">Team Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter team name"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Team Members (BGMI Usernames) *</label>
                <p style={{ 
                  fontSize: '0.85rem', 
                  color: 'var(--text-secondary)', 
                  marginBottom: '10px' 
                }}>
                  Add 2-4 members for your team
                </p>
                {newTeam.members.map((member, index) => (
                  <input
                    key={index}
                    type="text"
                    className="form-input"
                    placeholder={`Member ${index + 1} BGMI Username`}
                    value={member}
                    onChange={(e) => {
                      const newMembers = [...newTeam.members];
                      newMembers[index] = e.target.value;
                      setNewTeam({ ...newTeam, members: newMembers });
                    }}
                    style={{ marginBottom: '10px' }}
                  />
                ))}
              </div>

              <button type="submit" className="btn btn-success" style={{ width: '100%' }}>
                Create Team
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
